import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Paper,
  styled,
} from "@mui/material";
import {
  Person as PersonIcon,
  Description as DescriptionIcon,
  Apps as AppsIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  RateReview as RateReviewIcon,
  ArrowRightAlt as ArrowRightAltIcon,
} from "@mui/icons-material";

import { useLocation, useNavigate, Outlet } from "react-router-dom";
import ScheduleInterview from "../profile/ScheduleInterview";
import PersonalInfo from "../profile/PersonalInfo";
import CareerDetails from "../profile/CareerDetails";
import PreferredRoles from "../profile/PreferredRoles";
// import RoleExpertise from "../profile/RoleExpertise";
import ProfileReviewStatus from "../profile/ProfileReviewStatus";
import { useProfile } from "../../../contexts/ProfileContext";

import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CareerJourney from "../profile/CareerJourney";

const StyledPaper = styled(Paper)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  minHeight: "calc(100vh - 100px)",
  borderRadius: theme.spacing(2),
  overflow: "hidden",
  maxWidth: 1400,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  marginLeft: 0,
  marginRight: 0,
  boxShadow: "none",
}));

const Sidebar = styled(Box)(({ theme }) => ({
  width: 320,
  backgroundColor: theme.palette.background.default,
  borderRight: `1px solid ${theme.palette.divider}`,
  position: "relative",
  paddingTop: theme.spacing(4),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1),
  "& .MuiListItem-root": {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
}));

const Content = styled(Box)(({ theme }) => ({
  flex: 1,
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  backgroundColor: theme.palette.background.paper,
  minWidth: 600,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  "& .MuiContainer-root": {
    maxWidth: "none",
    padding: 0,
  },
}));

const VerticalLine = styled("div")(({ theme }) => ({
  position: "absolute",
  left: 32,
  top: 48,
  bottom: 32,
  width: 2,
  background: "rgba(108, 92, 231, 0.18)",
  zIndex: 0,
}));

const menuItems = [
  {
    text: "Complete profile",
    subtext: "Tell us about you",
    icon: <PersonIcon />,
    path: "/candidate/profile",
  },
  {
    text: "Enter career details",
    subtext: "Share your experience (CV or Linkedin profile)",
    icon: <DescriptionIcon />,
    path: "/candidate/profile/career-details",
  },
  {
    text: "Choose your target roles, choose roles you want to be considered for",
    subtext: "Select preferred roles",
    icon: <AppsIcon />,
    path: "/candidate/profile/preferred-roles",
  },
  // {
  //   text: "Role expertise",
  //   subtext:
  //     "You will need to answer few questions about the roles you have selected.",
  //   icon: <AssignmentIcon />,
  //   path: "/candidate/profile/role-expertise",
  // },
  {
    text: "Profile review and feedback by EQuAlly expert",
    subtext: " Reviewed by an EQuAlly expert",
    icon: <RateReviewIcon />,
    path: "/candidate/profile/review",
  },
  {
    text: " Meet with EQuAlly",
    subtext: "1:1 career conversation",
    icon: <ScheduleIcon />,
    path: "/candidate/profile/schedule",
  },
  {
    text: "Career Journey",
    subtext: "Your personalised journey",
    icon: <WorkOutlineIcon />,
    path: "/candidate/profile/career-journey",
  },
];

const CandidateProfileLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isUnderReview,
    isApprovedNotActive,
    isApprovedAndActive,
    isUpdateReview,
  } = useProfile();

  React.useEffect(() => {
    if (isUpdateReview) {
      const allowed = [
        "/candidate/profile/review",
        "/candidate/profile/career-journey",
      ];
      if (!allowed.includes(location.pathname)) {
        navigate("/candidate/profile/review", { replace: true });
      }
    }

    if (isApprovedNotActive) {
      const allowed = [
        // "/candidate/profile/review",
        "/candidate/profile/schedule",
      ];
      if (!allowed.includes(location.pathname)) {
        navigate("/candidate/profile/schedule", { replace: true });
      }
    }
  }, [isApprovedNotActive, location.pathname, navigate]);

  const handleStepClick = (path, idx) => {
    // Only allow navigation to enabled steps
    if (isStepEnabled(idx)) {
      navigate(path);
    }
  };

  // Determine which steps are enabled
  const isStepEnabled = (idx) => {
    const tempProfileData = JSON.parse(
      localStorage.getItem("tempProfileData") || "{}",
    );

    // If profile is under update review, disable all steps except review status
    if (isUpdateReview) {
      return idx === 3 || idx == 5 || idx === 6;
    }
    if (isUnderReview) {
      return idx === 3;
    }

    // If approved and active, enable all profile viewing steps (0-4) but disable 'Schedule interview' (5)
    if (isApprovedAndActive) {
      return idx <= 3 || idx === 5 || idx === 6; // Enable steps 0, 1, 2, 3, 4 (Complete profile, Career details, Preferred roles, Role expertise, Profile review)
    }
    // If approved but not active, only review (4) and schedule interview (5) are enabled
    if (isApprovedNotActive) {
      return idx === 4;
    }

    // Step 0 (Complete Profile) — always enabled
    if (idx === 0) return true;

    // Step 1 (Career Details) — enable if personalInfo exists
    if (idx === 1) return !!tempProfileData.personalInfo;

    // Step 2 (Preferred Roles) — enable if careerDetails exists
    if (idx === 2) return !!tempProfileData.careerDetails;

    // Step 3 (Profile Review) — enable if all three exist
    if (idx === 3) {
      return (
        tempProfileData.personalInfo &&
        tempProfileData.careerDetails &&
        tempProfileData.preferredRoles
      );
    }

    // For new users or users under review, enable all profile creation steps (0-4) but disable 'Schedule Interview' (5)
    if (idx === 4 || idx === 5 || idx === 6) {
      return false;
    }
    // Default: enable all profile creation steps for new users
    return true;
  };

  const journeySteps = [
    "Upload Profile",
    "Career Details",
    "Role Selection",
    "Expert Review",
    "1:1 EQuAlly",
    "Career Journey",
  ];

  const getActiveJourneyStep = () => {
    switch (location.pathname) {
      case "/candidate/profile":
        return 0;
      case "/candidate/profile/career-details":
        return 1;
      case "/candidate/profile/preferred-roles":
        return 2;
      case "/candidate/profile/review":
        return 3;
      case "/candidate/profile/schedule":
        return 4;
      case "/candidate/profile/career-journey":
        return 5;
      default:
        return 0;
    }
  };

  return (
    <StyledPaper sx={{ width: "100%", maxWidth: "100%" }} elevation={0}>
      <Sidebar sx={{ width: "30%" }}>
        <VerticalLine />
        <List>
          {menuItems.map((item, idx) => {
            const enabled = isStepEnabled(idx);
            return (
              <ListItem
                key={item.path}
                onClick={() => handleStepClick(item.path, idx)}
                selected={location.pathname === item.path}
                sx={{
                  flexDirection: "column",
                  alignItems: "flex-start",
                  opacity: enabled ? 1 : 0.4,
                  backgroundColor:
                    location.pathname === item.path
                      ? "rgba(108, 92, 231, 0.08)"
                      : "transparent",
                  borderLeft: (theme) =>
                    location.pathname === item.path
                      ? `4px solid ${theme.palette.primary.main}`
                      : "4px solid transparent",
                  cursor: enabled ? "pointer" : "not-allowed",
                  position: "relative",
                  zIndex: 1,
                  pointerEvents: enabled ? "auto" : "none",
                  "&:hover": {
                    backgroundColor: enabled
                      ? "rgba(108, 92, 231, 0.04)"
                      : "transparent",
                  },
                }}
                title={
                  !enabled && idx === 3
                    ? "Please complete all previous steps before submitting for review"
                    : ""
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                    position: "relative",
                    zIndex: 2,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: (theme) =>
                        location.pathname === item.path
                          ? theme.palette.primary.main
                          : "inherit",
                      position: "relative",
                      zIndex: 2,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle1"
                        fontWeight={location.pathname === item.path ? 600 : 400}
                      >
                        {item.text}
                      </Typography>
                    }
                  />
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ pl: 5, pr: 2 }}
                >
                  {item.subtext}
                </Typography>
              </ListItem>
            );
          })}
        </List>
      </Sidebar>
      <Content sx={{ width: "70%" }}>
        <Box sx={{ width: "100%", mb: 4, mt: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Let’s build your Career Journey
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              color: "text.secondary",
            }}
          >
            {journeySteps.map((step, index) => {
              const isActive = index === getActiveJourneyStep();
              const isLast = index === journeySteps.length - 1;

              return (
                <React.Fragment key={step}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: isActive ? 700 : 400,
                      color: isActive ? "primary.main" : "inherit",
                    }}
                  >
                    {step}
                  </Typography>
                  {!isLast && <ArrowRightAltIcon />}
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
        {(() => {
          switch (location.pathname) {
            case "/candidate/profile":
              return <PersonalInfo />;
            case "/candidate/profile/career-details":
              return <CareerDetails />;
            case "/candidate/profile/preferred-roles":
              return <PreferredRoles />;
            // case "/candidate/profile/role-expertise":
            //   return <RoleExpertise />;

            case "/candidate/profile/review":
              return <ProfileReviewStatus />;
            case "/candidate/profile/schedule":
              return <ScheduleInterview />;
            case "/candidate/profile/career-journey":
              return <CareerJourney />;
            default:
              return <div>Page not found</div>;
          }
        })()}
      </Content>
    </StyledPaper>
  );
};

export default CandidateProfileLayout;
